import React, { useState, useRef, useEffect } from 'react';
import { GPTService } from '../../services/gptService';
import { PrimeArtifact } from '../../types/primeArtifact';
import {
  PrimeArtifactState,
  PrimeArtifactStateTransition,
  PrimeProcess,
} from '../../types/primeModel';
import './GPTChat.css';

interface Message {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

interface GPTChatProps {
  onArtifactGenerated?: (artifact: PrimeArtifact) => void;
  onStatesGenerated?: (states: PrimeArtifactState[]) => void;
  onTransitionsGenerated?: (transitions: PrimeArtifactStateTransition[]) => void;
  onProcessesGenerated?: (processes: PrimeProcess[]) => void;
  onCompleteModelGenerated?: (model: Record<string, unknown>) => void;
  artifactId?: string;
  parentId?: string;
  artifactTypeId?: string;
}

const GPTChat: React.FC<GPTChatProps> = ({
  onArtifactGenerated,
  onStatesGenerated,
  onTransitionsGenerated,
  onProcessesGenerated,
  onCompleteModelGenerated,
  artifactId,
}) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [generationType, setGenerationType] = useState<
    'artifact' | 'states' | 'transitions' | 'processes' | 'complete-model'
  >('complete-model');
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSendMessage = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      role: 'user',
      content: input,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput('');
    setIsLoading(true);
    setError(null);

    try {
      let response;

      switch (generationType) {
        case 'artifact':
          response = await GPTService.getInstance().generateArtifact(input);
          if (onArtifactGenerated && response) {
            onArtifactGenerated(response);
          }
          break;
        case 'states':
          if (!artifactId) {
            throw new Error('Artifact ID is required for generating states');
          }
          response = await GPTService.getInstance().generateStates(artifactId, input);
          if (onStatesGenerated && response) {
            onStatesGenerated(response as PrimeArtifactState[]);
          }
          break;
        case 'transitions':
          if (!artifactId) {
            throw new Error('Artifact ID is required for generating transitions');
          }
          response = await GPTService.getInstance().generateTransitions(artifactId, input);
          if (onTransitionsGenerated && response) {
            onTransitionsGenerated(response as PrimeArtifactStateTransition[]);
          }
          break;
        case 'processes':
          if (!artifactId) {
            throw new Error('Artifact ID is required for generating processes');
          }
          response = await GPTService.getInstance().generateProcesses(artifactId, input);
          if (onProcessesGenerated && response) {
            onProcessesGenerated(response as PrimeProcess[]);
          }
          break;
        case 'complete-model':
          response = await GPTService.getInstance().generateCompleteModel(input);
          if (onCompleteModelGenerated && response) {
            onCompleteModelGenerated(response as Record<string, unknown>);
          }
          break;
      }

      const assistantMessage: Message = {
        role: 'assistant',
        content: (response as { message?: string }).message || 'Generation completed successfully.',
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'An error occurred');
      console.error('Error in GPT chat:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      void handleSendMessage();
    }
  };

  return (
    <div className="gpt-chat-container">
      <div className="gpt-chat-header">
        <h3>GPT Assistant</h3>
        <div className="gpt-generation-type-selector">
          <label htmlFor="generation-type">Generate: </label>
          <select
            id="generation-type"
            value={generationType}
            onChange={(e) =>
              setGenerationType(
                e.target.value as
                  | 'complete-model'
                  | 'artifact'
                  | 'states'
                  | 'transitions'
                  | 'processes'
              )
            }
            disabled={isLoading}
          >
            <option value="complete-model">Complete Model</option>
            <option value="artifact">Artifact</option>
            <option value="states">States</option>
            <option value="transitions">Transitions</option>
            <option value="processes">Processes</option>
          </select>
        </div>
      </div>

      <div className="gpt-chat-messages">
        {messages.length === 0 ? (
          <div className="gpt-chat-empty">
            <p>
              Start a conversation with the GPT assistant to generate artifacts, states,
              transitions, or processes.
            </p>
            <p>
              For best results, provide clear and detailed prompts about what you want to generate.
            </p>
          </div>
        ) : (
          messages.map((message, index) => (
            <div key={index} className={`gpt-message ${message.role}`}>
              <div className="gpt-message-content">{message.content}</div>
              <div className="gpt-message-timestamp">{message.timestamp.toLocaleTimeString()}</div>
            </div>
          ))
        )}
        <div ref={messagesEndRef} />
      </div>

      <div className="gpt-chat-input">
        <textarea
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type your prompt here..."
          disabled={isLoading}
        />
        <button
          onClick={handleSendMessage}
          disabled={isLoading || !input.trim()}
          className="gpt-send-button"
        >
          {isLoading ? 'Generating...' : 'Send'}
        </button>
      </div>

      {error && <div className="gpt-error">{error}</div>}
    </div>
  );
};

export default GPTChat;
